import { Accordion, Switch } from '@mantine/core';
import { camelCase } from 'lodash';
import { useLocalStorage } from 'react-use';
import { Json } from '../../types';
import { Group, GroupContent, GroupHeader, GroupTitle, Wrapper } from './Parameters.style';
import { camelCaseToSentenceCase } from './Parameters.utils';
import Parameter from './_parts/Parameter/Parameter';

export type ParametersProps = {
  id: string;
  data: Json;
  onChange: (id: string, value: string | number | boolean, isSilent?: boolean) => void;
  onSet: (id: string, value: string | number | boolean) => void;
  children?: JSX.Element;
};

export function Parameters(props: ParametersProps) {
  const { data } = props;

  const [value, setValue] = useLocalStorage('parameters', ['base']);

  function onAccordion(value: string[] | null) {
    if (value === null) {
      setValue([]);
      return;
    }

    setValue(value);
  }

  function onSet(id: string, value: string | number | boolean) {
    props.onSet(id, value);
  }

  function onToggleGroup(ev: any, group: Json) {
    const key = `${camelCase(group.id)}.enabled`;
    ev.preventDefault();
    ev.stopPropagation();
    props.onSet(key, !group.isEnabled);
    props.onChange(key, !group.isEnabled, true);
  }

  function renderItem(item: Json, index: number) {
    const key = [item.id, index, props.id].join('-');
    return <Parameter key={key} onChange={props.onChange} onSet={onSet} param={item} />;
  }

  function renderItems(items: Json[]) {
    return items.map((item: Json, index) => renderItem(item, index));
  }

  function renderGroupCheckbox(group: Json) {
    if (!group.isToggleable) return null;
    return (
      <Switch size='xs' checked={group.isEnabled} onChange={(ev) => onToggleGroup(ev, group)} />
    );
  }

  function renderGroup(group: Json, index: number) {
    const { name } = group;

    if (group.items.length === 0) return null;

    return (
      <Group key={group.id + ' ' + index} className='group' value={group.id}>
        <GroupHeader>
          <GroupTitle>
            {camelCaseToSentenceCase(name)}
            {renderGroupCheckbox(group)}
          </GroupTitle>
        </GroupHeader>
        <GroupContent>
          {renderItems(group.items)}
          {props.children}
        </GroupContent>
      </Group>
    );
  }

  function renderGroups() {
    return Object.values(data).map((group: Json, index: number) => renderGroup(group, index));
  }

  return (
    <Wrapper className='Parameters-wrapper' data-testid='Parameters-wrapper'>
      <Accordion multiple={true} value={value} onChange={onAccordion}>
        {renderGroups()}
      </Accordion>
    </Wrapper>
  );
}

export default Parameters;
